import { MANDATORY_CHANNELS_MESSAGE, type GateChannel } from "@bing/shared";

/**
 * THE GAME-SERVER'S CHANNEL REFUSAL, READ OFF THE WIRE (gate-table-refusal, 2026-10-09).
 *
 * The api and the game-server each keep their own membership cache (a «member» answer lives 10
 * minutes), so `/me` can say 200 while a table join is refused by `assertChannelMember`. That
 * refusal used to reach the screen as an ordinary join failure: the gate sentence, «…در کانال‌های
 * زیر عضو شوید:», over no channels and no button.
 *
 * TWO FORMS ARE ACCEPTED (the contract proposed to card-db):
 *  (a) today's: exactly {@link MANDATORY_CHANNELS_MESSAGE};
 *  (b) extended: that sentence as the FIRST line, then one `<title>\t<url>` line per unmet channel.
 * The first line must match exactly (trailing whitespace / CRLF tolerated). A malformed line is
 * skipped, and a url that is not an `https://t.me/` link is NEVER accepted: it becomes a button.
 */

/** `https://t.me/<username>`, `https://t.me/+<hash>` or `https://t.me/joinchat/<hash>` — nothing else. */
const TME_URL = /^https:\/\/t\.me\/(?:\+[\w-]+|joinchat\/[\w-]+|\w+)$/;

function messageOf(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return "";
}

/**
 * The channels a game-server channel refusal names — `[]` for form (a) or a (b) with no usable line
 * — or `null` when the message is not a channel refusal at all (every other join failure).
 */
export function parseChannelRefusal(error: unknown): GateChannel[] | null {
  // Split on LF alone: a CRLF's `\r` is trailing whitespace, which every read below trims.
  const lines = messageOf(error).split("\n");
  if (lines[0]?.trimEnd() !== MANDATORY_CHANNELS_MESSAGE) return null;
  const channels: GateChannel[] = [];
  for (const line of lines.slice(1)) {
    const tab = line.indexOf("\t");
    if (tab < 0) continue;
    const title = line.slice(0, tab).trim();
    const url = line.slice(tab + 1).trim();
    if (!title || !TME_URL.test(url)) continue;
    if (channels.some((c) => c.chatId === url)) continue;
    channels.push({ chatId: url, username: null, title, inviteLink: url, joinUrl: null });
  }
  return channels;
}

/** Whether a join failure is the game-server's channel refusal (either form). */
export function isChannelRefusal(error: unknown): boolean {
  return parseChannelRefusal(error) !== null;
}
